import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Device } from 'src/api/device';
import { DataTable } from 'src/components/DataTable';
import { ActionMenu } from 'src/components/DataTable/ActionMenu';
import { Td } from 'src/components/DataTable/Td';
import { Status } from 'src/components/Status';
import { useGlobalModals } from 'src/contexts/global-modals';
import { links } from 'src/router/routes';

const columns = [
  {
    key: 'id',
    label: 'ID',
  },
  {
    key: 'playwall_id',
    label: 'Name',
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'created_at',
    label: 'Created at',
  },
  {
    key: 'updated_at',
    label: 'Last updated at',
  },
];

type Props = {
  devices: Device[];
  isLoading: boolean;
};

export const DevicesDataTable = ({ devices, isLoading }: Props) => {
  const navigate = useNavigate();

  const { openDeleteDeviceModal, openRestartDeviceModal } = useGlobalModals();

  const handleRowClick = useCallback(
    (deviceId: number) => {
      navigate(`${links.device}/${deviceId}`);
    },
    [navigate]
  );

  const getContent = useCallback(
    (device: Device) => {
      return (
        <>
          <Td>{device.id}</Td>
          <Td>{device.playwall_id}</Td>
          <Td>{device?.status && <Status status={device.status} />}</Td>
          <Td>{new Date(device.created_at).toLocaleDateString()}</Td>
          <Td>
            <span>{new Date(device.updated_at).toLocaleDateString()}</span>
            <ActionMenu
              onView={() => navigate(`${links.device}/${device.id}`)}
              onRemove={() => openDeleteDeviceModal(device.id)}
              onRestart={() => openRestartDeviceModal(device.id)}
            />
          </Td>
        </>
      );
    },
    [openDeleteDeviceModal, openRestartDeviceModal, navigate]
  );

  const getRows = useCallback((device: Device) => {
    return {
      created_at: device.created_at,
      id: String(device.id),
      playwall_id: device.playwall_id,
      status: device.status,
    };
  }, []);

  const data = useMemo(() => {
    return (
      devices?.map((device) => ({
        content: getContent(device),
        onRowClick: () => handleRowClick(device.id),
        rows: getRows(device),
      })) || []
    );
  }, [devices, getContent, getRows, handleRowClick]);

  return <DataTable columns={columns} data={data} isLoading={isLoading} />;
};
